import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, Download, ArrowLeft, Clock, X } from "lucide-react";
import AdminShell from "./AdminShell";
import { cjCategories, cjProductDetail, cjRewrite, cjImport, errMessage } from "../../lib/api";
import { eur } from "../../config";

const BACKOFF = [30, 60, 120];

export default function ImportWizard() {
  const [params] = useSearchParams();
  const pid = params.get("pid");
  const initialName = params.get("name") || "";
  const navigate = useNavigate();

  const [cats, setCats] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [detailErr, setDetailErr] = useState("");
  const [rewriting, setRewriting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  const [form, setForm] = useState({
    display_name: initialName,
    category: "Robes",
    subcategory: "",
    display_short_description: "",
    display_description: "",
    tags: "",
    is_featured: false,
    publication_status: "draft",
    manual_shipping_cost: "",
  });
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { cjCategories().then((d) => setCats(d.categories)).catch(() => {}); }, []);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const scheduleRetry = (seconds, category) => {
    clearInterval(timerRef.current);
    setCountdown(seconds);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); loadDetail(category, true); return null; }
        return c - 1;
      });
    }, 1000);
  };

  const cancelRetry = () => { clearInterval(timerRef.current); setCountdown(null); attemptsRef.current = 0; };

  const loadDetail = async (category, isAuto = false) => {
    if (!isAuto) { attemptsRef.current = 0; clearInterval(timerRef.current); setCountdown(null); }
    setLoadingDetail(true); setDetailErr("");
    try {
      const d = await cjProductDetail(pid, category, form.manual_shipping_cost);
      setDetail(d); attemptsRef.current = 0; setCountdown(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && detail?.code === "CJ_RATE_LIMIT") {
        const secs = BACKOFF[Math.min(attemptsRef.current, BACKOFF.length - 1)];
        attemptsRef.current += 1;
        scheduleRetry(secs, category);
      } else {
        setDetailErr(errMessage(e, "Impossible de charger le produit CJ"));
      }
    }
    setLoadingDetail(false);
  };
  useEffect(() => { if (pid) loadDetail(form.category); /* eslint-disable-next-line */ }, [pid]);
  useEffect(() => { if (pid && detail) loadDetail(form.category); /* eslint-disable-next-line */ }, [form.category]);
  useEffect(() => {
    if (!pid || !detail) return;
    const t = setTimeout(() => loadDetail(form.category, true), 600);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [form.manual_shipping_cost]);

  const generate = async () => {
    setRewriting(true);
    try {
      const res = await cjRewrite({ product_name: form.display_name || detail?.name_en, raw_description: detail?.description || "", category: form.category });
      setVal("display_description", res.description);
      toast.success("Description générée !");
    } catch (e) {
      toast.error(errMessage(e, "Génération IA indisponible"));
    }
    setRewriting(false);
  };

  const doImport = async (publish = false) => {
    if (!form.display_name.trim()) { toast.error("Le nom est requis"); return; }
    setImporting(true);
    try {
      const payload = {
        pid,
        display_name: form.display_name,
        category: form.category,
        subcategory: form.subcategory || null,
        display_short_description: form.display_short_description,
        display_description: form.display_description,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        is_featured: form.is_featured,
        publication_status: publish ? "published" : form.publication_status,
        manual_shipping_cost: form.manual_shipping_cost === "" ? null : parseFloat(form.manual_shipping_cost),
      };
      await cjImport(payload);
      toast.success(publish ? "Produit importé et publié !" : "Produit importé dans votre boutique !");
      navigate("/admin/products");
    } catch (e) {
      toast.error(errMessage(e, "Import échoué"));
    }
    setImporting(false);
  };

  return (
    <AdminShell title="Importer un produit">
      <button onClick={() => navigate("/admin/cj-catalog")} className="flex items-center gap-1.5 text-sm text-ardoise hover:text-encre mb-5" data-testid="wizard-back">
        <ArrowLeft size={15} /> Retour au catalogue
      </button>

      {!pid && <p className="text-red-600 text-sm">Aucun produit sélectionné.</p>}

      {countdown !== null && (
        <div className="border border-dore/40 bg-dore/10 text-encre text-sm p-4 mb-4 flex items-center justify-between gap-3" data-testid="wizard-retry-banner">
          <span className="flex items-center gap-2">
            <Clock size={16} className="text-dore animate-pulse" />
            Limite de requêtes CJ atteinte. Nouvelle tentative automatique dans <strong>{countdown}s</strong>…
          </span>
          <button onClick={cancelRetry} className="flex items-center gap-1 text-xs text-ardoise hover:text-encre" data-testid="wizard-retry-cancel">
            <X size={14} /> Annuler
          </button>
        </div>
      )}

      {loadingDetail && countdown === null && <p className="text-ardoise text-sm">Chargement du produit CJ...</p>}
      {detailErr && <div className="border border-red-300 bg-red-50 text-red-700 text-sm p-4" data-testid="wizard-detail-error">{detailErr}</div>}

      {detail && (
        <div data-testid="import-wizard">
          <div className="mb-6 bg-rose/10 border border-dore/30 p-4">
            <label className="text-xs uppercase tracking-wider text-ardoise/70 flex items-center justify-between gap-2">
              <span>Nom du produit dans la boutique <span className="text-red-500">*</span></span>
              <span className="text-[10px] normal-case text-ardoise/50 truncate max-w-[50%]" title={detail.name_en}>Nom CJ : {detail.name_en || "—"}</span>
            </label>
            <input value={form.display_name} onChange={(e) => setVal("display_name", e.target.value)}
              autoComplete="off" name="cj-display-name" placeholder="Ex: Robe Dentelle Noire Élégance" required
              className="w-full border border-dore/40 bg-white px-4 py-3 text-base outline-none focus:border-dore mt-2" data-testid="wizard-name" />
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
          {/* Aperçu produit + variantes */}
          <div>
            <div className="aspect-square overflow-hidden bg-rose/10 border border-dore/20 mb-4">
              {detail.main_image ? <img src={detail.main_image} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <p className="text-xs text-ardoise/70 mb-1">Nom original CJ</p>
            <p className="text-sm text-encre mb-4">{detail.name_en}</p>

            <h3 className="font-display text-lg text-encre mb-2">Variantes & prix calculés</h3>
            <div className="border border-dore/20" data-testid="wizard-variants">
              <div className="grid grid-cols-5 text-[11px] uppercase tracking-wider text-ardoise/60 bg-rose/10 px-3 py-2">
                <span>Variante</span><span>Coût</span><span>Vente</span><span>Port CJ</span><span>Marge nette</span>
              </div>
              {detail.variants.map((v) => (
                <div key={v.cj_variant_id} className="grid grid-cols-5 text-xs px-3 py-2 border-t border-dore/10" data-testid="wizard-variant-row">
                  <span className="truncate">{v.variant_key || v.sku}</span>
                  <span>{v.cost_eur != null ? eur(v.cost_eur) : "-"}</span>
                  <span className="text-dore font-medium">{v.pricing_preview ? eur(v.pricing_preview.selling_price) : "-"}</span>
                  <span>{v.pricing_preview ? eur(v.pricing_preview.shipping_cost_estimate) : "-"}</span>
                  <span className={v.pricing_preview && v.pricing_preview.net_margin_amount < 0 ? "text-red-500 font-medium" : "text-green-600 font-medium"}>{v.pricing_preview ? eur(v.pricing_preview.net_margin_amount) : "-"}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-ardoise/60 mt-2">Le port est facturé séparément au client au paiement (selon le poids). La « marge nette » indique votre profit si vous absorbiez le port fournisseur CJ.</p>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest text-ardoise">Frais de port réels CJ (optionnel, €)</label>
              <input type="number" step="0.01" min="0" value={form.manual_shipping_cost}
                onChange={(e) => setVal("manual_shipping_cost", e.target.value)}
                placeholder="Estimé au poids si vide"
                className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore mt-1"
                data-testid="wizard-manual-shipping" />
              <p className="text-[11px] text-ardoise/60 mt-1">Saisissez le coût de livraison réel indiqué par CJ pour affiner le calcul de marge.</p>
            </div>
          </div>

          {/* Formulaire de personnalisation */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-ardoise/70">Catégorie *</label>
                <select value={form.category} onChange={(e) => setVal("category", e.target.value)} className="w-full border border-dore/30 px-3 py-2.5 text-sm outline-none focus:border-dore mt-1 bg-white" data-testid="wizard-category">
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ardoise/70">Sous-catégorie</label>
                <input value={form.subcategory} onChange={(e) => setVal("subcategory", e.target.value)} className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore mt-1" data-testid="wizard-subcategory" />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-ardoise/70">Description courte (SEO)</label>
              <input value={form.display_short_description} onChange={(e) => setVal("display_short_description", e.target.value)} className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore mt-1" data-testid="wizard-short-desc" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-wider text-ardoise/70">Description française</label>
                <button onClick={generate} disabled={rewriting} className="flex items-center gap-1.5 text-xs text-dore hover:text-noir disabled:opacity-50" data-testid="wizard-generate-btn">
                  {rewriting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {rewriting ? "Génération..." : "Générer avec l'IA"}
                </button>
              </div>
              <textarea value={form.display_description} onChange={(e) => setVal("display_description", e.target.value)} rows={7} className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore" data-testid="wizard-description" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-ardoise/70">Tags (séparés par virgule)</label>
              <input value={form.tags} onChange={(e) => setVal("tags", e.target.value)} placeholder="été, chic, soirée" className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore mt-1" data-testid="wizard-tags" />
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-encre cursor-pointer">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setVal("is_featured", e.target.checked)} data-testid="wizard-featured" /> Produit vedette
              </label>
              <select value={form.publication_status} onChange={(e) => setVal("publication_status", e.target.value)} className="border border-dore/30 px-3 py-2 text-sm bg-white" data-testid="wizard-status">
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
              </select>
            </div>

            <button onClick={() => doImport(false)} disabled={importing} className="w-full bg-noir text-white py-3.5 text-xs uppercase tracking-widest font-semibold hover:bg-ardoise transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="wizard-import-btn">
              {importing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Importer (brouillon)
            </button>
            <button onClick={() => doImport(true)} disabled={importing} className="w-full bg-dore text-noir py-3.5 text-xs uppercase tracking-widest font-semibold hover:bg-dore/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="wizard-import-publish-btn">
              {importing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Importer et publier
            </button>
          </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
